import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS } from "./eisfSignatureRequestEmailTags";

function basePayload(userData, overrides = {}) {
  return {
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    ActionType: "",
    ...overrides,
  };
}

function resolveTemplate(template, defaultValue) {
  const custom = String(template ?? "").trim();
  if (custom) return custom;
  return String(defaultValue ?? "").trim();
}

function configStr(row, pascalKey, camelKey) {
  const v = row?.[pascalKey] ?? row?.[camelKey];
  return v == null ? "" : String(v);
}

function getApiDataset(response) {
  if (!response || typeof response !== "object") return null;
  const d = response.data ?? response.Data;
  return d && typeof d === "object" ? d : null;
}

function unwrapRows(table) {
  if (!table) return [];
  if (Array.isArray(table)) return table;
  if (Array.isArray(table.Rows)) return table.Rows;
  if (Array.isArray(table.rows)) return table.rows;
  return [];
}

function getFirstAvailableTable(ds, names) {
  if (!ds) return [];
  if (Array.isArray(ds.Tables)) {
    for (const name of names) {
      const found = ds.Tables.find(
        (t) => String(t?.TableName || t?.tableName || "").toLowerCase() === name.toLowerCase(),
      );
      if (found) {
        const rows = unwrapRows(found);
        if (rows.length) return rows;
      }
    }
    for (const t of ds.Tables) {
      const rows = unwrapRows(t);
      if (rows.length) return rows;
    }
  }
  if (ds.Tables && typeof ds.Tables === "object" && !Array.isArray(ds.Tables)) {
    for (const name of names) {
      const key = Object.keys(ds.Tables).find((k) => k.toLowerCase() === name.toLowerCase());
      if (key) {
        const rows = unwrapRows(ds.Tables[key]);
        if (rows.length) return rows;
      }
    }
  }
  for (const name of names) {
    if (ds[name]) {
      const rows = unwrapRows(ds[name]);
      if (rows.length) return rows;
    }
  }
  return [];
}

function buildFallback() {
  return {
    signatureRequestEmailSubjectTemplate: "",
    signatureRequestEmailBodyTemplate: "",
    signatureRequestEmailSubjectDefault: EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.subject,
    signatureRequestEmailBodyDefault: EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.body,
    signatureRequestEmailSubjectDisplay: EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.subject,
    signatureRequestEmailBodyDisplay: EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.body,
  };
}

export async function fetchEisfConfig(userData) {
  try {
    const res = await api.post(
      API_ENDPOINTS.ISF.CONFIG_GET,
      basePayload(userData, { ActionType: "GetConfig" }),
    );
    if (Number(res?.success) !== 1) {
      return buildFallback();
    }
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["EisfConfig", "eisfConfig", "Table", "Table1"]);
    if (!Array.isArray(rows) || !rows[0]) {
      return buildFallback();
    }
    const row = rows[0];
    const subjectTpl = configStr(
      row,
      "SignatureRequestEmailSubjectTemplate",
      "signatureRequestEmailSubjectTemplate",
    );
    const bodyTpl = configStr(
      row,
      "SignatureRequestEmailBodyTemplate",
      "signatureRequestEmailBodyTemplate",
    );
    const subjectDef =
      configStr(
        row,
        "SignatureRequestEmailSubjectDefault",
        "signatureRequestEmailSubjectDefault",
      ) || EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.subject;
    const bodyDef =
      configStr(
        row,
        "SignatureRequestEmailBodyDefault",
        "signatureRequestEmailBodyDefault",
      ) || EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.body;

    return {
      signatureRequestEmailSubjectTemplate: subjectTpl,
      signatureRequestEmailBodyTemplate: bodyTpl,
      signatureRequestEmailSubjectDefault: subjectDef,
      signatureRequestEmailBodyDefault: bodyDef,
      signatureRequestEmailSubjectDisplay: resolveTemplate(subjectTpl, subjectDef),
      signatureRequestEmailBodyDisplay: resolveTemplate(bodyTpl, bodyDef),
    };
  } catch (_e) {
    return buildFallback();
  }
}

export async function saveEisfConfig(payload, userData) {
  const requestPayload = basePayload(userData, {
    ActionType: "SaveConfig",
    SignatureRequestEmailSubjectTemplate:
      String(payload?.signatureRequestEmailSubjectTemplate ?? "").trim() || null,
    SignatureRequestEmailBodyTemplate:
      String(payload?.signatureRequestEmailBodyTemplate ?? "").trim() || null,
  });
  const res = await api.post(API_ENDPOINTS.ISF.CONFIG_SAVE, requestPayload);
  return {
    success: Number(res?.success) === 1,
    message: res?.message || "eISF configuration saved successfully.",
  };
}
